import { Buttons } from "@neon-purge/shared";
import { el } from "../ui/dom.js";
import { audio } from "./audio.js";
import { isTouchDevice } from "./touch.js";

/**
 * TRAINING campaign: three guided solo stages that teach every mechanic and
 * every button (keyboard AND gamepad). Entirely client-side — stages are
 * plain solo rooms; the director watches inputs/state and advances steps.
 */

export const TUTORIAL_STAGES: { mapId: string; bots: number; title: string }[] = [
  { mapId: "grid_arena", bots: 0, title: "BASIC TRAINING" },
  { mapId: "neo_island", bots: 0, title: "MOBILITY & VEHICLES" },
  { mapId: "neon_district", bots: 1, title: "FINAL EXAM" },
];

let stage = -1;
export function tutorialStage(): number { return stage; }
export function setTutorialStage(n: number): void { stage = n; }

// set ONLY when the director finishes a stage — distinguishes "stage
// complete → next stage" from "player pressed Leave → abort the campaign"
let completed = false;
export function markStageComplete(): void { completed = true; }
export function consumeStageComplete(): boolean { const c = completed; completed = false; return c; }

export interface TutCtx {
  x: number; y: number; z: number;
  yaw: number;
  /** OR of all buttons pressed during the frame's sim steps. */
  buttons: number;
  onGround: boolean;
  driving: boolean;
  me: { kills?: number; hasJetpack?: boolean; rocketAmmo?: number; parachute?: boolean; grenades?: number } | null;
}

interface Store { [k: string]: number }

interface Step {
  text: string; // what to do (or the info-card title)
  kb: string; // keyboard binding
  pad: string; // gamepad binding
  /** touch-controls wording (phone); falls back to kb when unset */
  tc?: string;
  /** step is impossible on touch (no button for it) — skipped there */
  skipTouch?: boolean;
  /** INFO CARD: explanation lines shown instead of a task — the step
   *  completes on a Jump press ("got it"). Teaches the RULES, not buttons. */
  info?: string[];
  done: (c: TutCtx, s: Store, edge: (b: number) => boolean) => boolean;
}

/** Rule-explainer card: advance with Jump (Space / Ⓐ). */
const infoCard = (title: string, lines: string[]): Step => ({
  text: title,
  kb: "Space — got it",
  pad: "Ⓧ / Ⓐ — got it",
  tc: "tap this card — got it",
  info: lines,
  // touch: the director sets s.tapGo when the card itself is tapped
  done: (c, s, edge) => edge(Buttons.Jump) || s.tapGo === 1,
});

const STAGE_STEPS: Step[][] = [
  [
    {
      text: "Move around", kb: "W A S D", pad: "Left stick", tc: "drag the LEFT side (virtual stick)",
      done: (c, s) => {
        if (s.mx === undefined) { s.mx = c.x; s.mz = c.z; s.md = 0; }
        s.md += Math.hypot(c.x - s.mx, c.z - s.mz);
        s.mx = c.x; s.mz = c.z;
        return s.md > 12;
      },
    },
    {
      text: "Look around", kb: "Mouse", pad: "Right stick", tc: "drag the RIGHT side",
      done: (c, s) => {
        if (s.ly === undefined) { s.ly = c.yaw; s.la = 0; }
        const d = Math.abs(c.yaw - s.ly);
        s.la += Math.min(d, Math.PI * 2 - d);
        s.ly = c.yaw;
        return s.la > 3;
      },
    },
    {
      text: "Jump 3 times", kb: "Space", pad: "Ⓧ / Ⓐ", tc: "👟 button ×3",
      done: (c, s, edge) => {
        if (edge(Buttons.Jump)) s.j = (s.j ?? 0) + 1;
        return (s.j ?? 0) >= 3;
      },
    },
    {
      text: "Sprint while moving", kb: "hold Shift", pad: "click L3", tc: "push the move stick to the RIM",
      done: (c, s) => {
        if ((c.buttons & Buttons.Sprint) !== 0) s.sp = (s.sp ?? 0) + 1;
        return (s.sp ?? 0) > 60;
      },
    },
    {
      text: "Crouch (toggle)", kb: "C", pad: "Ⓑ / ◯", tc: "⤓ button (tap on / tap off)",
      done: (c, s, edge) => edge(Buttons.Crouch),
    },
    {
      text: "Fire your weapon (5 shots)", kb: "Left click", pad: "R2 / RT", tc: "tap the screen or hold 🔥",
      done: (c, s, edge) => {
        if (edge(Buttons.Fire)) s.f = (s.f ?? 0) + 1;
        return (s.f ?? 0) >= 5;
      },
    },
    {
      text: "Aim down sights and fire", kb: "hold Right click", pad: "hold L2 / LT", tc: "🎯 to aim, then 🔥",
      done: (c) => (c.buttons & Buttons.ADS) !== 0 && (c.buttons & Buttons.Fire) !== 0,
    },
    {
      text: "Reload", kb: "R", pad: "▢ / X", skipTouch: true,
      done: (c, s, edge) => edge(Buttons.Reload),
    },
    {
      text: "Knife attack", kb: "Alt", pad: "click R3", tc: "🔪 button",
      done: (c, s, edge) => edge(Buttons.Knife),
    },
    {
      text: "Throw a grenade", kb: "E", pad: "RB / R1", tc: "🧨 button",
      done: (c, s, edge) => edge(Buttons.Grenade),
    },
    {
      text: "Use a health shot", kb: "1 / ↑", pad: "D-pad ↑", skipTouch: true,
      done: (c, s, edge) => edge(Buttons.Heal),
    },
    infoCard("THE GUN GAME LADDER", [
      "🔫 Every KILL with your ladder gun = promoted to the NEXT weapon (13 tiers)",
      "📦 Each new weapon arrives FULLY LOADED",
      "🔪 Get KNIFED and you DROP one tier",
      "🏆 A kill with the FINAL weapon — the thrown BALLISTIC FANG — WINS the match",
    ]),
    infoCard("POWER-UP WEAPONS", [
      "🚀 Rockets, grenades and vehicles KILL but never PROMOTE",
      "🪜 Only your LADDER GUN climbs the ladder — pick your moments",
    ]),
    infoCard("ITEMS & BONUSES", [
      "✚ Health shots are STORED — press 1 / ↑ to heal when hurt",
      "🧨 Grenades are stored too — throw with E / 2",
      "🛡 Armor absorbs damage before your health",
      "⚡ Overdrive · ☠ Overcharge · 👻 Cloak are instant, timed boosts",
      "🧰 Chests grant a JETPACK or ROCKETS",
    ]),
  ],
  [
    {
      text: "Ride a jump pad (glowing disc)", kb: "walk onto it", pad: "walk onto it",
      done: (c, s) => {
        if (!c.driving && c.y > 6) s.pad = 1;
        return s.pad === 1;
      },
    },
    {
      text: "Deploy the paraglider while falling", kb: "double-tap Space", pad: "double-tap Ⓧ", tc: "double-tap 👟 while falling",
      done: (c) => !!c.me?.parachute,
    },
    {
      text: "Open a chest (jetpack or rockets)", kb: "walk into it", pad: "walk into it",
      done: (c) => !!c.me?.hasJetpack || (c.me?.rocketAmmo ?? 0) > 0,
    },
    infoCard("JETPACK FLIGHT", [
      "🎒 Double-tap SPACE / Ⓧ in the air to FLY — dive to gain speed",
      "⏹ A single press STOPS the flight (double-tap again to re-fly)",
      "🔥 Hold Shift / L3 for the AFTERBURNER",
      "🪂 No jetpack? The same double-tap opens your PARAGLIDER",
    ]),
    infoCard("SWIMMING (Pirate Ship)", [
      "🌊 Walk into the sea to swim — full 3D underwater combat",
      "⬇ DIVE: hold C / Ⓑ · on phone tap ⤓ (tap again to FLOAT up)",
      "⬆ Idle floats you to the surface; Space / Ⓧ kicks up faster",
      "🦈 Dive to the seabed for wrecks + sunken village loot",
    ]),
    {
      text: "Get in a vehicle", kb: "F near it", pad: "Ⓨ / △ near it", tc: "✋ near it",
      done: (c) => c.driving,
    },
    {
      text: "Fire a vehicle rocket", kb: "Left click", pad: "R2 / RT", tc: "tap the screen / 🔥",
      done: (c, s, edge) => c.driving && edge(Buttons.Fire),
    },
    {
      text: "Exit the vehicle", kb: "F", pad: "Ⓨ / △", tc: "✋",
      done: (c, s) => {
        if (c.driving) s.drove = 1;
        return s.drove === 1 && !c.driving;
      },
    },
  ],
  [
    infoCard("GAME MODES", [
      "🔫 GUN GAME — the weapon-ladder race (this is the main mode)",
      "💀 FFA — pure deathmatch, most frags wins",
      "🧟 PURGE — co-op zombie survival: kills pay POINTS, spend them at the GOLD SHOPS",
      "🛸 ARENA — Mario-Kart-style bomb battle, first to the stars wins",
    ]),
    infoCard("READY FOR THE EXAM", [
      "🎯 Gun kills PROMOTE you — knife kills DEMOTE the victim",
      "💡 Watch the bottom-right: your weapon, its class and your ammo",
      "🏆 Now eliminate the training bot. Good hunting.",
    ]),
    {
      text: "Eliminate the training bot — good hunting", kb: "everything you learned", pad: "everything you learned",
      done: (c) => (c.me?.kills ?? 0) >= 1,
    },
  ],
];

export class TutorialDirector {
  private panel: HTMLElement;
  private stepIdx = 0;
  private store: Store = {};
  private prevButtons = 0;
  private doneAt = 0;
  private stageDone = false;

  constructor(hudRoot: HTMLElement, private stageIndex: number, private onStageDone: () => void) {
    this.panel = el("div", {
      style:
        "position:absolute;top:9%;left:50%;transform:translateX(-50%);z-index:60;" +
        "background:rgba(6,10,18,0.88);border:1px solid #00f0ff;padding:14px 26px;" +
        "text-align:center;letter-spacing:0.06em;min-width:min(420px,90vw);max-width:min(720px,92vw);pointer-events:none",
    });
    hudRoot.append(this.panel);
    // phone: info cards are advanced by tapping the card itself (the 👟
    // route still works) — "can't click got it" had no touch answer before
    this.panel.addEventListener("touchstart", (e) => {
      const step = STAGE_STEPS[this.stageIndex][this.stepIdx];
      if (!step?.info) return;
      e.preventDefault();
      this.store.tapGo = 1;
    }, { passive: false });
    this.skipImpossible();
    this.render();
  }

  /** Phone: steps with no touch control (crouch, reload) are skipped. */
  private skipImpossible(): void {
    if (!isTouchDevice()) return;
    const steps = STAGE_STEPS[this.stageIndex];
    while (this.stepIdx < steps.length && steps[this.stepIdx].skipTouch) this.stepIdx++;
  }

  /** Binding hint for the current input world: touch, or keyboard+pad. */
  private bindLine(step: Step): string {
    return isTouchDevice() ? `👆 ${step.tc ?? step.kb}` : `⌨ ${step.kb}    🎮 ${step.pad}`;
  }

  private render(check = false): void {
    const steps = STAGE_STEPS[this.stageIndex];
    const st = TUTORIAL_STAGES[this.stageIndex];
    if (this.stageDone) {
      this.panel.replaceChildren(
        el("div", { style: "color:#9dff00;font-size:1.3em;font-weight:bold", text: `✔ ${st.title} COMPLETE` }),
        el("div", { style: "color:#8a95a5;margin-top:6px", text: this.stageIndex + 1 < TUTORIAL_STAGES.length ? "next stage incoming…" : "TRAINING COMPLETE — you're ready" }),
      );
      return;
    }
    const step = steps[this.stepIdx];
    // the panel is click-through except when a card must be tappable (phone)
    this.panel.style.pointerEvents = isTouchDevice() && step.info ? "auto" : "none";
    if (step.info && !check) {
      // RULE CARD: teaches the game's logic (ladder, items, modes) — bigger,
      // left-aligned lines, advanced with Jump so reading is self-paced
      this.panel.replaceChildren(
        el("div", { style: "color:#ff2fd0;font-size:0.8em", text: `TRAINING ${this.stageIndex + 1}/${TUTORIAL_STAGES.length} — ${st.title} · step ${this.stepIdx + 1}/${steps.length}` }),
        el("div", { style: "color:#ffd166;font-size:1.25em;margin:8px 0;letter-spacing:0.15em", text: step.text }),
        el("div", { style: "text-align:left;display:inline-block;margin:2px 0 8px" },
          ...step.info.map((line) => el("div", { style: "color:#e8f6ff;font-size:0.95em;margin:4px 0", text: line })),
        ),
        el("div", { style: "color:#7ff3ff", text: this.bindLine(step) }),
      );
      return;
    }
    this.panel.replaceChildren(
      el("div", { style: "color:#ff2fd0;font-size:0.8em", text: `TRAINING ${this.stageIndex + 1}/${TUTORIAL_STAGES.length} — ${st.title} · step ${this.stepIdx + 1}/${steps.length}` }),
      el("div", { style: `color:${check ? "#9dff00" : "#e8f6ff"};font-size:1.25em;margin:8px 0`, text: check ? "✔ done" : step.text }),
      el("div", { style: "color:#7ff3ff", text: this.bindLine(step) }),
    );
  }

  /** Call once per rendered frame with the latest sim context. */
  frame(c: TutCtx): void {
    if (this.stageDone) return;
    const now = performance.now();
    if (this.doneAt) {
      if (now - this.doneAt > 900) {
        this.doneAt = 0;
        this.stepIdx++;
        this.skipImpossible(); // phone: hop over crouch/reload (no buttons)
        this.store = {};
        if (this.stepIdx >= STAGE_STEPS[this.stageIndex].length) {
          this.stageDone = true;
          this.render();
          audio.win();
          setTimeout(() => { markStageComplete(); this.onStageDone(); }, 2200);
        } else {
          this.render();
        }
      }
      return;
    }
    const edge = (b: number): boolean => (c.buttons & b) !== 0 && (this.prevButtons & b) === 0;
    const step = STAGE_STEPS[this.stageIndex][this.stepIdx];
    if (step.done(c, this.store, edge)) {
      this.doneAt = now;
      this.render(true);
      audio.promote();
    }
    this.prevButtons = c.buttons;
  }

  dispose(): void {
    this.panel.remove();
  }
}
